import axios from 'axios';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

import { getEasternDay } from '@utils';

import { convertShopAdvisorToTeamUser } from './store-utils';
import {
  ITeamProfile,
  IShopDetail,
  ITeamProfileStore,
  AddTeamMemberResponse,
  ITeamUserUpdate,
  ITeamProductionOverviewDataV2,
} from './team-profile-store-types';

/**
 * @file Team Profile Store
 *
 * Centralized Zustand store for managing all state and actions related to
 * the user's team profile information.
 *
 * Responsibilities:
 * - Holds core team profile state
 * - Provides actions for fetching and updating team data
 * - Manages team member permissions and CRUD operations
 */
export const useTeamProfileStore = create<ITeamProfileStore>()(
  subscribeWithSelector(
    devtools(
      (set, get) => ({
        isLoadingTeamProfile: true,
        isLoadingShop: false,
        isLoadingProductionOverview: false,
        canEditTeam: false,
        selectedShop: undefined,
        teamProfile: undefined,
        productionOverview: undefined,
        userTeams: [],
        shop: undefined,

        setIsLoadingTeamProfile: (isLoading: boolean) => {
          set(() => ({
            isLoadingTeamProfile: isLoading,
          }));
        },

        setIsLoadingShop: (isLoading: boolean) => {
          set(
            () => ({
              isLoadingShop: isLoading,
            }),
            false,
            'setIsLoadingShop',
          );
        },

        fetchTeamProfile: async (teamId: string, userId?: string) => {
          get().setIsLoadingTeamProfile(true);
          try {
            const res = await axios.get(`${TEAMS_SERVICE_URL}/teams/${teamId}`);
            const teamData = res?.data as ITeamProfile;

            const resState = {
              teamProfile: teamData,
              canEditTeam: false,
            };

            if (teamData?.users && userId) {
              const selectedUser = teamData.users.find((user) => user.userId.toLowerCase() === userId.toLowerCase());
              resState.canEditTeam = Boolean(selectedUser?.isAdmin || selectedUser?.isLeader);
            }

            set(
              {
                ...resState,
                isLoadingTeamProfile: false,
              },
              false,
              'fetchTeamProfile/success',
            );

            return res?.data as ITeamProfile;
          } catch (error) {
            console.error('Error fetching team profile:', error);
            set({ isLoadingTeamProfile: false }, false, 'fetchTeamProfile/error');
            return undefined;
          }
        },

        fetchShopData: async (): Promise<void> => {
          const { teamProfile } = get();
          const shopId = teamProfile?.shopId;

          if (!shopId) {
            console.warn('No shopId found in teamProfile');
            return;
          }

          get().setIsLoadingShop(true);
          try {
            const response = await axios.get(`${SHOP_SERVICE_URL}/v1/shops/${shopId}`);
            const shopData = response?.data as IShopDetail;

            set(
              {
                shop: shopData,
                isLoadingShop: false,
              },
              false,
              'fetchShopData/success',
            );
          } catch (error) {
            console.error('Error fetching shop data:', error);
            set({ isLoadingShop: false }, false, 'fetchShopData/error');
          }
        },

        updateTeamMember: async (teamId: string, userId: string, userToTeamId: string, data: ITeamUserUpdate) => {
          try {
            const { teamProfile } = get();
            if (!teamProfile) return;

            const user = teamProfile.users.find((user) => user.userId === userId);
            if (!user) {
              throw new Error('User not found');
            }

            const { isAdmin, isLeader } = user;

            // We want to preserve the roles that are not being updated
            // and only update the field specified in the data object
            const payload = {
              isAdmin,
              isLeader,
              ...data,
            };

            await axios.put(`${TEAMS_SERVICE_URL}/teams/${teamId}/members/${userToTeamId}`, payload, {
              withCredentials: true,
            });

            const updatedUsers = teamProfile.users.map((user) => {
              if (user.userId === userId) {
                return { ...user, ...payload };
              }
              return user;
            });

            set(
              {
                teamProfile: {
                  ...teamProfile,
                  users: updatedUsers,
                },
              },
              false,
              'updateTeamMember/success',
            );
          } catch (error) {
            console.error('Failed to update team member:', error);
            throw error;
          }
        },

        makeAdmin: async (teamId: string, userId: string, userToTeamId: string) => {
          return get().updateTeamMember(teamId, userId, userToTeamId, { isAdmin: true });
        },

        removeAdmin: async (teamId: string, userId: string, userToTeamId: string) => {
          return get().updateTeamMember(teamId, userId, userToTeamId, { isAdmin: false });
        },

        makeLead: async (teamId: string, userId: string, userToTeamId: string) => {
          return get().updateTeamMember(teamId, userId, userToTeamId, { isLeader: true });
        },

        removeLead: async (teamId: string, userId: string, userToTeamId: string) => {
          return get().updateTeamMember(teamId, userId, userToTeamId, { isLeader: false });
        },

        removeFromTeam: async (teamId: string, userId: string, userToTeamId: string) => {
          try {
            await axios.delete(`${TEAMS_SERVICE_URL}/teams/${teamId}/members/${userToTeamId}`, {
              params: { effectiveLeaveDate: getEasternDay() },
              withCredentials: true,
            });

            const { teamProfile } = get();
            if (!teamProfile) return;

            const updatedUsers = teamProfile.users.filter((user) => user.userId !== userId);

            set(
              {
                teamProfile: {
                  ...teamProfile,
                  users: updatedUsers,
                },
              },
              false,
              'removeFromTeam/success',
            );
          } catch (error) {
            console.error('Failed to remove team member:', error);
            throw error;
          }
        },

        addTeamMembers: async (userIds: string[]) => {
          const { teamProfile, shop } = get();
          if (!teamProfile) {
            throw new Error('Team profile not loaded');
          }

          const teamId = teamProfile.teamId;

          try {
            const payload = {
              members: userIds.map((userId) => ({ userId })),
            };

            const response: { data: AddTeamMemberResponse[] } = await axios.post(`${TEAMS_SERVICE_URL}/teams/${teamId}/members`, payload, {
              withCredentials: true,
            });

            if (!response?.data) {
              throw new Error('Failed to add team members');
            }

            const userToTeamIdMap = response.data.reduce<Record<string, string>>((acc, member) => {
              acc[member.userId] = member.userToTeamId;
              return acc;
            }, {});

            const newTeamMembers = shop?.advisors.filter((user) => userIds.includes(user.userId));
            const formatedNewTeamMembers = newTeamMembers?.map((user) => {
              return convertShopAdvisorToTeamUser({ ...user, teamId, userToTeamId: userToTeamIdMap[user.userId] });
            });

            set(
              {
                teamProfile: {
                  ...teamProfile,
                  users: [...teamProfile.users, ...formatedNewTeamMembers],
                },
              },
              false,
              'addTeamMembers/success',
            );
          } catch (error) {
            console.error('Failed to add team members:', error);
            throw error; // Re-throw to allow handling in the component
          }
        },

        updateTeam: async (teamId, updateData) => {
          try {
            const response = await axios.put(`${TEAMS_SERVICE_URL}/teams/${teamId}`, updateData, {
              headers: {
                'Content-Type': 'application/json',
              },
              withCredentials: true,
            });

            // For successful updates, the API returns a 20* response
            if (response.status >= 200 && response.status < 300) {
              // Update the team profile data locally
              const { teamProfile } = get();
              if (teamProfile) {
                const updatedProfile = { ...teamProfile, ...updateData };
                set({ teamProfile: updatedProfile }, false, 'updateTeam/success');
              }
              return;
            }

            return;
          } catch (error) {
            console.error('Error updating team:', error);
            throw error;
          }
        },

        /**
         * @description
         * Updates the team's about information. It's a simple wrapper for updateTeam,
         * but it ensures that only the bio and personalMedia fields are updated.
         * @param teamId - The ID of the team to update
         * @param payload - The payload containing the bio and/or personalMedia fields
         */
        updateAbout: async (teamId, payload) => {
          await get().updateTeam(teamId, payload);
        },

        deleteTeam: async (teamId: string) => {
          try {
            await axios.delete(`${TEAMS_SERVICE_URL}/teams/${teamId}`, {
              withCredentials: true,
            });
          } catch (error) {
            console.error('Failed to delete team:', error);
            throw error;
          }
        },

        fetchTeamProductionOverview: async (teamId, year) => {
          set({ isLoadingProductionOverview: true });
          try {
            const response = await axios.get(`${PERSONNEL_SERVICE_URL}/teams/${teamId}/production-overview/${year}`);
            const data = response?.data as ITeamProductionOverviewDataV2;

            set({ productionOverview: { ...data, _fetchedForYear: year } }, false, 'fetchTeamProductionOverview/success');
          } catch (error) {
            console.error('Error fetching team production overview:', error);
            return null;
          } finally {
            set({ isLoadingProductionOverview: false });
          }
        },
      }),
      { name: 'TeamProfileStore' },
    ),
  ),
);
